import { useCallback, useState } from 'react'
import { ArrivalRouteArgs } from '../../containers/TowingRecordPage/components/routes/subforms/StartRoute/types'
import { getDirections, getReverseGeocodeResult } from '../../components/googlemaps'

interface EtaCalculationOptions {
  destination?: ArrivalRouteArgs
}

export const useEtaCalculation = ({ destination }: EtaCalculationOptions, onEta: (eta: Date) => void) => {
  const [loading, setLoading] = useState(false)

  const calculate = useCallback(async () => {
    if (!destination || loading) return
    setLoading(true)
    try {
      const position = await getCurrentPositionAsync()
      const { latitude, longitude } = position.coords

      const geocodeResult = await getReverseGeocodeResult({ lat: latitude, lng: longitude })
      if (!geocodeResult) {
        console.error('Failed to reverse geocode current position')
        return
      }

      const origin = {
        address: geocodeResult.address,
        city: geocodeResult.city ?? null,
        lat: latitude,
        long: longitude,
      }

      const directions = await getDirections(origin, {
        address: destination.address,
        city: destination.city ?? null,
        lat: destination.lat ?? null,
        long: destination.long ?? null,
      })
      const duration = directions?.routes?.[0]?.legs?.[0]?.duration?.value
      if (!duration) {
        console.error('Failed to get route duration')
        return
      }

      const eta = new Date()
      eta.setSeconds(eta.getSeconds() + duration)
      onEta(eta)
    } finally {
      setLoading(false)
    }
  }, [destination, loading, onEta])

  return { calculate, loading }
}

function getCurrentPositionAsync(options?: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, options))
}
